import { AnyEventObject } from 'xstate';
import { sendDeleteConfirmation } from '~/features/utils';
import { createEmptyEducationForm, createEmptyTrainingForm, emptyEducationForm } from './constants';
import {
  DeleteEvent,
  EditEducationEvent,
  EditTrainingEvent,
  EducationResponseEvent,
  EducationsResponseEvent,
  EduDetailResponseEvent,
  SetSpecBoardOptions,
  SetSpecCertOptions,
  SetSubSpecCertOptions,
  TrainingResponseEvent,
  TrainingsResponseEvent,
  WizNextPageEvent,
  WizPrevPageEvent,
} from './events';
import { AcademicMachineContext as Context, EducationFormValues, Scope } from './types';

export const updateFacilityForm = (scope: Scope): Partial<Context> => {
  // I'm unsure what event is firing here, but I'm refactoring the machine to match patterns.
  const { facility } = scope.event as AnyEventObject;
  return {
    trainingForm: {
      ...scope.context.trainingForm,
      facility: facility,
    },
  };
};

export const closeModalAndResetForms = (scope: Scope): Partial<Context> => {
  return {
    trainingForm: createEmptyTrainingForm(),
    // MCV-694: fresh clone so the education form's details subsection doesn't
    // carry over from the previous entry on the next open.
    educationForm: createEmptyEducationForm(),
    openEditTrainingModal: false,
    modal: {
      ...scope.context.modal,
      edit: false,
      create: false,
      detailModal: false,
    },
  };
};

export const setEducations = (scope: Scope): Partial<Context> => {
  const event = scope.event as EducationsResponseEvent;
  return {
    educations: event.output,
  };
};

export const setTrainings = (scope: Scope): Partial<Context> => {
  const event = scope.event as TrainingsResponseEvent;
  return {
    trainings: event.output,
  };
};

export const setEducationForm = (scope: Scope): Partial<Context> => {
  const event = scope.event as EditEducationEvent;
  const foundEducationForm =
    scope.context.educations.find((education) => education.id === event.payload.id) ||
    emptyEducationForm;
  return {
    educationForm: foundEducationForm,
  };
};

export const setIdToDelete = (scope: Scope): Partial<Context> => {
  const event = scope.event as DeleteEvent;
  return {
    _idToDelete: event.payload.id,
  };
};

export const setModalToEdit = (scope: Scope): Partial<Context> => {
  return {
    modal: {
      ...scope.context.modal,
      edit: true,
      create: false,
    },
  };
};

export const setModalToCreate = (scope: Scope): Partial<Context> => {
  return {
    modal: {
      ...scope.context.modal,
      edit: false,
      create: true,
    },
  };
};

export const openDetailModal = (scope: Scope): Partial<Context> => {
  return {
    modal: {
      ...scope.context.modal,
      detailModal: true,
    },
  };
};

export const closeDetailModal = (scope: Scope): Partial<Context> => {
  return {
    modal: {
      ...scope.context.modal,
      detailModal: false,
    },
  };
};

export const addEducation = (scope: Scope): Partial<Context> => {
  const event = scope.event as EducationResponseEvent;
  return {
    educations: [...scope.context.educations, event.output],
  };
};

export const deleteEduDetail = (scope: Scope): Partial<Context> => {
  const { output } = scope.event as EduDetailResponseEvent;
  const openEducationId = scope.context.educationForm.id;
  const removeDeletedDetail = (education: EducationFormValues) => ({
    ...education,
    details: education.details.filter((detail) => detail._id !== output.id),
  });

  return {
    educationForm: removeDeletedDetail(scope.context.educationForm),
    educations:
      openEducationId == null
        ? scope.context.educations
        : scope.context.educations.map((education) =>
            education.id === openEducationId ? removeDeletedDetail(education) : education
          ),
  };
};

export const removeEducation = (scope: Scope): Partial<Context> => {
  const filteredEducations = scope.context.educations.filter((education) => {
    return education.id !== scope.context._idToDelete;
  });
  return {
    educations: filteredEducations,
  };
};
export const updatedEducation = (scope: Scope): Partial<Context> => {
  const { output: updatedEducation } = scope.event as EducationResponseEvent;
  const untouchedEducations = scope.context.educations.filter(
    (education) => education.id !== updatedEducation.id
  );
  untouchedEducations.push(updatedEducation);
  return {
    educations: untouchedEducations,
  };
};

export const setSpecBoardOptions = (scope: Scope): Partial<Context> => {
  const event = scope.event as SetSpecBoardOptions;
  return {
    _specialtyBoardOptions: event.values,
  };
};

export const setSubCertOptions = (scope: Scope): Partial<Context> => {
  const event = scope.event as SetSpecCertOptions;
  return {
    _specSubCertOptions: event.values,
  };
};

export const setSpecCertOptions = (scope: Scope): Partial<Context> => {
  const event = scope.event as SetSubSpecCertOptions;
  return {
    _specCertOptions: event.values,
  };
};

export const resetTrainingWizard = (): Partial<Context> => {
  return {
    trainingWizard: {
      open: false,
      currentPage: 0,
    },
  };
};

export const openTrainingWizard = (): Partial<Context> => {
  return {
    trainingForm: createEmptyTrainingForm(),
    trainingWizard: {
      open: true,
      currentPage: 0,
    },
  };
};

export const addTraining = (scope: Scope): Partial<Context> => {
  const { output: newTraining } = scope.event as TrainingResponseEvent;
  return {
    trainings: [...scope.context.trainings, newTraining],
  };
};

export const removeTraining = (scope: Scope): Partial<Context> => {
  const filteredTraining = scope.context.trainings.filter((training) => {
    return training.id !== scope.context._idToDelete;
  });
  return {
    trainings: filteredTraining,
  };
};

export const closeEditTrainingModel = (scope: Scope): Partial<Context> => {
  return {
    openEditTrainingModal: false,
  };
};

export const openEditTrainingModel = (scope: Scope): Partial<Context> => {
  return {
    openEditTrainingModal: true,
  };
};

export const updateTrainings = (scope: Scope): Partial<Context> => {
  const { output: updatedTraining } = scope.event as TrainingResponseEvent;
  const trainings = scope.context.trainings.filter((training) => training.id != updatedTraining.id);
  trainings.push(updatedTraining);
  return {
    trainings: trainings,
  };
};

export const setTrainingForm = (scope: Scope): Partial<Context> => {
  const { payload } = scope.event as EditTrainingEvent;
  return {
    trainingForm:
      scope.context.trainings.find((training) => training.id === payload.id) ||
      createEmptyTrainingForm(),
  };
};

/**
 * Handles both trainingForm and trainingWizard since they are so closely linked
 * @param scope
 * @returns
 */
export const handleTrainingWizardNext = (scope: Scope): Partial<Context> => {
  const { values } = scope.event as WizNextPageEvent;
  const currentPage =
    scope.context.trainingWizard.currentPage >= 3
      ? scope.context.trainingWizard.currentPage
      : scope.context.trainingWizard.currentPage + 1;
  return {
    trainingForm: {
      ...scope.context.trainingForm,
      ...values,
    },
    trainingWizard: {
      open: true,
      currentPage,
    },
  };
};

export const handleTrainingWizardPrev = (scope: Scope): Partial<Context> => {
  const { values } = scope.event as WizPrevPageEvent;
  const currentPage =
    scope.context.trainingWizard.currentPage == 0
      ? scope.context.trainingWizard.currentPage
      : scope.context.trainingWizard.currentPage - 1;
  return {
    trainingForm: {
      ...scope.context.trainingForm,
      ...(values || {}),
    },
    trainingWizard: {
      open: true,
      currentPage,
    },
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const openDeleteConfirmation = (args: any) => {
  const { self, event, system } = args;
  const onConfirm = (id: number, type: 'TRAINING' | 'EDUCATION') => {
    self.send({ type: 'ON_DELETE', payload: { id, type } });
  };
  // Send confirmation to userState (which handles the confirmation dialog)
  const userActor = system.get('userState');
  if (userActor) {
    userActor.send(sendDeleteConfirmation(() => onConfirm(event.payload.id, event.payload.type)));
  } else {
    console.warn('[academics] Unable to send confirmation - userState not found in system');
  }
};
